import { AxiosResponse, AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { TagApiError } from '../utils/analytics-tagging';
import {
  IReturnEligibilityRequest,
  IReturnEligibilityResponse,
  IReturnEligibilityV2Request,
} from '../models/api';
import { ReturnEligibilityAPI, ReturnEligibilityV2API } from '../api/returns';
import { IGenericError } from '../models/errors';
import { checkIsInStoreOnlyReturn } from '../components/return-method/ReturnMethodHelper';
import { adaptV2EligibilityResponse } from '../utils/eligibility-utils';
import { useReturnStore } from '../stores/returnStore';

export const GetReturnsEligibilityDetails = (
  requestData: IReturnEligibilityRequest,
  setReturnEligibilityDetails: (data: IReturnEligibilityResponse) => void,
) =>
  ReturnEligibilityAPI(requestData)
    .then((response: AxiosResponse<IReturnEligibilityResponse>) => {
      setReturnEligibilityDetails(response.data);
      return response.data;
    })
    .catch((error: AxiosError<IGenericError>) => {
      const isNetworkError =
        error?.response?.status === 500 ||
        error?.code === 'ERR_NETWORK' ||
        error?.code === 'ECONNABORTED';
      TagApiError(error);

      if (isNetworkError) {
        throw new Error('NETWORK');
      }
      throw new Error('GENERIC');
    });

/**
 * ROS v2 eligibility fetch. Calls the consolidated v2 endpoint,
 * adapts the returnability-only response into the internal shape, and stores it.
 * Recoverable "order cannot be evaluated" (SSR_COM_RETURN_ELIGIBILITY_ERROR) and
 * transport failures surface the existing retry error, so no item is shown
 * returnable on the initial answer alone.
 */
export const GetReturnsEligibilityV2Details = (
  requestData: IReturnEligibilityV2Request,
  net: string,
  setReturnEligibilityDetails: (data: IReturnEligibilityResponse) => void,
) =>
  ReturnEligibilityV2API(requestData)
    .then((response) => {
      if (response.data?.errors?.length) {
        // Recoverable "cannot evaluate" (e.g. SSR_COM_RETURN_ELIGIBILITY_ERROR)
        // arrives as a 200 with errors[], not an AxiosError — shape a minimal
        // error so it is tagged for analytics like the other error paths.
        TagApiError({
          response: { data: response.data, status: response.status },
        } as unknown as AxiosError<IGenericError>);
        throw new Error('GENERIC');
      }
      const adapted = adaptV2EligibilityResponse(response.data, net);
      setReturnEligibilityDetails(adapted);
      return adapted;
    })
    .catch((error: AxiosError<IGenericError>) => {
      // Re-throw the handled "cannot evaluate" case without re-tagging.
      if (error instanceof Error && error.message === 'GENERIC') throw error;

      const isNetworkError =
        error?.response?.status === 500 ||
        error?.code === 'ERR_NETWORK' ||
        error?.code === 'ECONNABORTED';
      TagApiError(error);

      if (isNetworkError) {
        throw new Error('NETWORK');
      }
      throw new Error('GENERIC');
    });

/**
 * Returns true if the order is only eligible for in-store returns (not UPS or LORP eligible)
 */
export const useIsInStoreOnlyReturn = () => {
  const orderDetailsV2 = useReturnStore((state) => state.orderDetailsV2);
  const returnEligibilityDetails = useReturnStore(
    (state) => state.returnEligibilityDetails,
  );
  const [isInStoreOnlyReturn, setIsInStoreOnlyReturn] = useState(false);

  useEffect(() => {
    if (returnEligibilityDetails && orderDetailsV2) {
      const isInStoreOnly = checkIsInStoreOnlyReturn(
        orderDetailsV2,
        returnEligibilityDetails,
      );
      setIsInStoreOnlyReturn(isInStoreOnly);
    }
  }, [returnEligibilityDetails]);

  return isInStoreOnlyReturn;
};
